import React, { useState, useEffect, useCallback } from 'react'
import { FaEdit, FaTrash, FaSyncAlt } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import api from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import SearchBar from '../../components/SearchBar'
import Pagination from '../../components/Pagination'
import EmptyState from '../../components/EmptyState'
import GenericModal from '../../components/GenericModal'

const VisitaDescarregamento = () => {
    const [visitas, setVisitas] = useState([])
    const [pagina, setPagina] = useState(1)
    const [totalPaginas, setTotalPaginas] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [filtroAtivo, setFiltroAtivo] = useState(false)
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
    const navigate = useNavigate()
    const [modalConfig, setModalConfig] = useState(null)

    const handleSort = (key) => {
        let direction = 'asc'
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc'
        }
        setSortConfig({ key, direction })
    }

    const sortedVisitas = [...visitas].sort((a, b) => {
        if (sortConfig.key === 'unit') {
            const aValue = a.unit?.name.toLowerCase()
            const bValue = b.unit?.name.toLowerCase()
            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
            return 0
        }
        return 0
    })

    const handleAbrirModalEdicao = visita => {
        setModalConfig({
            title: 'Liberar Entrada',
            initialData: {
                ...visita,
                vt: visita.vt?.toString() || '',
                visit_status: 'Aguardando Liberação'
            },
            fieldsConfig: [
                { name: 'epi_helmet', label: 'EPI Capacete', type: 'text', disabled: true },
                { name: 'epi_vest', label: 'EPI Colete', type: 'text', disabled: true },
                { name: 'epi_boot', label: 'EPI Calçado Fechado', type: 'text', disabled: true },
                { name: 'entry_forecast', label: 'Data e Hora da Previsão', type: 'datetime-local', required: true },
                { name: 'vt', label: 'Numero da VT', type: 'text', required: false },
                { name: 'obs_vt', label: 'Observações sobre a VT', type: 'text', required: false }
            ],
            apiEndpoint: '/api/front/visits',
            method: 'put'
        })
    }

    const handleDeleteVisitaLiberada = async visitaId => {
        if (window.confirm('Tem certeza que deseja excluir esta visita?')) {
            setModalConfig({
                title: 'Confirmar Exclusão',
                initialData: { uuid: visitaId },
                fieldsConfig: [
                    {
                        name: 'reason',
                        label: 'Informe o motivo da exclusão!',
                        type: 'text',
                        required: true
                    }
                ],
                apiEndpoint: '/api/front/visits',
                method: 'delete'
            })
        }
    }

    const fetchVisitas = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const token = localStorage.getItem('token')
            if (!token) {
                navigate('/login')
                return
            }

            const visitas = 'Aguardando Agendamento'
            const motivo = 'descarregamento'
            const endpoint = searchTerm
                ? `/api/front/visits/search?query=${encodeURIComponent(searchTerm)}&page=${pagina}`
                : `/api/front/visits/get/${visitas}/${motivo}?page=${pagina}`

            const response = await api.get(endpoint, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            })

            setVisitas(response.data.data || [])
            setTotalPaginas(response.data.totalPages || 1)
            setTotalItems(response.data.totalRecords || 0)
            setFiltroAtivo(!!searchTerm)
        } catch (error) {
            console.error('Erro ao buscar visitas:', error)
            setError('Erro ao carregar visitas. Tente novamente mais tarde.')
            toast.error('Erro ao carregar visitas')
        } finally {
            setLoading(false)
        }
    }, [pagina, searchTerm, navigate])

    useEffect(() => {
        fetchVisitas()
    }, [fetchVisitas])

    const handleSearch = term => {
        setSearchTerm(term)
        setPagina(1)
    }

    const handleClearSearch = () => {
        setSearchTerm('')
        setPagina(1)
    }

    const handlePaginaAnterior = () => {
        if (pagina > 1) setPagina(pagina - 1)
    }

    const handleProximaPagina = () => {
        if (pagina < totalPaginas) setPagina(pagina + 1)
    }

    const handleAtualizarLista = () => {
        fetchVisitas()
    }

    return (
        <div className='listar-visitas-container'>
            <div className='container-drivers'>
                <div className='header-section'>
                    <h2>Descarregamentos Aguardando Agendamentos</h2>
                    <div className='controls'>
                        <SearchBar
                            onSearch={handleSearch}
                            onClear={handleClearSearch}
                            placeholder='Buscar por CG ou NF...'
                        />
                        <button
                            className='refresh-button'
                            onClick={handleAtualizarLista}
                            disabled={loading}
                            title='Atualizar lista'
                        >
                            <FaSyncAlt />
                        </button>
                    </div>
                </div>

                {error && <div className='error-message'>{error}</div>}

                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        <div className='summary-info'>
                            <span>Total: {totalItems} visitas </span>
                            {filtroAtivo && (
                                <span className='filter-active'>
                                    Filtro ativo - {visitas.length} resultados
                                    <button onClick={handleClearSearch} className='clear-filter'>
                                        Limpar filtro
                                    </button>
                                </span>
                            )}
                        </div>

                        {visitas.length === 0 ? (
                            <EmptyState
                                message={
                                    searchTerm
                                        ? 'Nenhuma visita encontrada com esse critério'
                                        : 'Nenhuma visita cadastrada ainda'
                                }
                            />
                        ) : (
                            <div className='table-responsive'>
                                <table className='table'>
                                    <thead>
                                        <tr>
                                            <th onClick={() => handleSort('unit')} style={{ cursor: 'pointer' }}>
                                                Unidade {sortConfig.key === 'unit' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                                            </th>
                                            <th>Hr. Chegada</th>
                                            <th>Vai carregar <br /> nas duas unidades</th>
                                            <th>Já está carregado</th>
                                            <th>Motorista</th>
                                            <th>Telefone</th>
                                            <th>Transportadora</th>
                                            <th>Motivo Visita</th>
                                            <th>CG/NF</th>
                                            <th>Placa</th>
                                            <th>Status</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedVisitas.map(visita => (
                                            <tr key={visita.uuid}>
                                                <td>{visita.unit.name}</td>
                                                <td>
                                                    {visita.arrival_date
                                                        ? (() => {
                                                            const date = new Date(visita.arrival_date)
                                                            const day = date.getUTCDate().toString().padStart(2, '0')
                                                            const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
                                                            const year = date.getUTCFullYear()
                                                            const hours = date.getUTCHours().toString().padStart(2, '0')
                                                            const minutes = date.getUTCMinutes().toString().padStart(2, '0')
                                                            return `${day}/${month} ${hours}:${minutes}`
                                                        })()
                                                        : 'Sem data informada'}
                                                </td>
                                                <td>{visita.to_load === true ? "Sim" : "Não"}</td>
                                                <td>{visita.loaded === true ? "Sim" : "Não"}</td>
                                                <td>{visita.visita_uuid.name.split(' ').slice(0, 2).join(' ') || ''}</td>
                                                <td>{visita.visita_uuid.phone}</td>
                                                <td>{visita.carrier}</td>
                                                <td>{visita.operation}</td>
                                                <td>{visita.number_cg}</td>
                                                <td>{visita.plate}</td>
                                                <td>
                                                    {visita.entry_forecast
                                                        ? (() => {
                                                            const date = new Date(visita.entry_forecast)
                                                            const day = date.getUTCDate().toString().padStart(2, '0')
                                                            const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
                                                            const year = date.getUTCFullYear()
                                                            const hours = date.getUTCHours().toString().padStart(2, '0')
                                                            const minutes = date.getUTCMinutes().toString().padStart(2, '0')
                                                            return `${day}/${month}/${year} ${hours}:${minutes}`
                                                        })()
                                                        : 'Aguardando Agendamento'}
                                                </td>
                                                <td className='actions-cell'>
                                                    <button
                                                        className='edit-button'
                                                        onClick={() => handleAbrirModalEdicao(visita)}
                                                        title='Editar'
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        className='delete-button'
                                                        onClick={() =>
                                                            handleDeleteVisitaLiberada(visita.uuid)
                                                        }
                                                        title='Excluir'
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {totalPaginas > 1 && (
                            <Pagination
                                currentPage={pagina}
                                totalPages={totalPaginas}
                                onPreviousPage={handlePaginaAnterior}
                                onNextPage={handleProximaPagina}
                                onPageChange={page => setPagina(page)}
                            />
                        )}
                    </>
                )}
            </div>

            {modalConfig && (
                <GenericModal
                    title={modalConfig.title}
                    initialData={modalConfig.initialData}
                    fieldsConfig={modalConfig.fieldsConfig}
                    apiEndpoint={modalConfig.apiEndpoint}
                    method={modalConfig.method}
                    onClose={() => setModalConfig(null)}
                    onSuccess={fetchVisitas}
                />
            )}
        </div>
    )
}

export default VisitaDescarregamento